import React from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "store/index";
import { handleUpdateFields } from "store/cms/pages/list/slice";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import JoditWysiwyg from "components/wysiwyg/JoditWysiwyg";
import DatePicker from "react-datepicker";
import moment from "moment";
import cn from "classnames";
import ReadOnlyInput from "components/inputs/ReadOnlyInput/ReadOnlyInput";
import { getDatePickerFormats, useTimeFormat } from "helpers/timeFormat";

interface PagesMainTabProps {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  readOnly?: boolean;
}

const PagesMainTab: React.FC<PagesMainTabProps> = ({
  fieldErrors,
  clearFieldError,
  readOnly
}) => {
  const { t } = useTranslation();
  const datePickerFormats = getDatePickerFormats(useTimeFormat());
  const dispatch = useAppDispatch();

  const payload = useAppSelector((store) => store["cms-pages"].payload);

  const handleData = (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => {
    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: img ?? value }));
  };

  return (
    <div className="pages-form-content mt-14">
      <div className="row">
        <div className="pages-form-box--item">
          <InnerInput
            value={payload?.title ?? ""}
            placeholder={t("enterTitle")}
            label={t("title")}
            type="text"
            id="pages-title"
            onChange={(e) => {
              handleData("title", e);
              clearFieldError("title");
            }}
            hasError={Boolean(fieldErrors?.["title"])}
            disabled={readOnly}
          />
        </div>
        <div className="pages-form-box--item">
          <InnerInput
            value={payload?.slug ?? ""}
            placeholder={t("enterSlug")}
            label={t("slug")}
            type="text"
            id="pages-slug"
            onChange={(e) => {
              handleData("slug", e);
              clearFieldError("slug");
            }}
            hasError={Boolean(fieldErrors?.["slug"])}
            disabled={readOnly}
          />
        </div>
        <div className="pages-form-box--item">
          <div
            className={cn("date-picker-wrapper", {
              "has-error": fieldErrors?.["effectiveFrom"],
            })}
          >
            <div className="title">{t("effectiveFrom")}</div>

            <DatePicker
              customInput={<ReadOnlyInput />}
              isClearable
              locale="en-GB"
              showTimeSelect
              timeFormat={datePickerFormats.timeFormat}
              placeholderText={datePickerFormats.placeholder}
              dateFormat={datePickerFormats.dateFormat}
              selected={
                payload?.effectiveFrom
                  ? moment(
                      payload?.effectiveFrom,
                      "DD.MM.YYYY HH:mm",
                      true,
                    ).toDate()
                  : null
              }
              onChange={(date) => {
                if (!date) {
                  handleData("effectiveFrom", "");
                  return;
                }

                handleData(
                  "effectiveFrom",
                  moment(date).format("DD.MM.YYYY HH:mm"),
                );

                clearFieldError("effectiveFrom");
              }}
              disabled={readOnly}
            />
          </div>
        </div>
        <div className="pages-form-box--item">
          <SwitchBtn
            activeTxt={t("active")}
            isActive={!!payload?.isActive}
            handleClick={() =>
              handleData(
                "isActive",
                payload?.isActive ? !payload?.isActive : true,
              )
            }
            disabled={readOnly}
          />
        </div>
      </div>
      <div className="pages-form-box--item html-editor">
        <div className="label">{t("content")}</div>
        <JoditWysiwyg
          value={payload?.content ?? ""}
          onChange={(html) => {
            handleData("content", html);
            clearFieldError("content");
          }}
          hasError={Boolean(fieldErrors?.["content"])}
          disabled={readOnly}
        />
      </div>
    </div>
  );
};

export default PagesMainTab;
